import { ObservableHistory } from 'mobx-observable-history';
import ActionStore from './ActionStore';
import HeaderTabStore from './HeaderTabStore';
import MenuStore from './MenuStore';
import ModalStore from './ModalStore';
import ToolStore from './ToolStore';
import PublicStore from './PublicStore';
import RouterStore from './RouterStore';
import WaitQueueStore from './WaitQueueStore';
import WaitAsyncQueueStore from './WaitAsyncQueueStore';
import FavoriteMenuStore from './FavoriteMenuStore';
import { t } from './TranslateStore';
import { MenuModel } from '../models';
import { installAgent } from '../../agent/install';
import { installRecorder } from '../../agent/recorder';
import AgentStore from './AgentStore';
import { attachDiagnosis, attachTarget } from '../../agent/attachTarget';

// eslint-disable-next-line import/prefer-default-export
export default function createStores(history: ObservableHistory) {
  const routerStore = new RouterStore(history);
  const menuStore = new MenuStore();
  const headerTabStore = new HeaderTabStore(routerStore);
  const waitQueueStore = new WaitQueueStore();
  const waitAsyncQueueStore = new WaitAsyncQueueStore();
  const publicStore = new PublicStore(headerTabStore, routerStore, waitQueueStore, waitAsyncQueueStore, menuStore);
  const actionStore = new ActionStore(publicStore, waitQueueStore);
  const modalStore = new ModalStore(publicStore, actionStore);
  const agentStore = new AgentStore(publicStore);
  const toolStore = new ToolStore(publicStore, routerStore, modalStore, agentStore);
  const favoriteMenuStore = new FavoriteMenuStore(publicStore, actionStore);

  // t 는 모듈 싱글톤이다. 컴포넌트가 store 주입 없이 t.T() 를 쓰기 때문에
  // Provider 에 넣는 인스턴스도 반드시 같은 객체여야 한다.
  t.setPublicStore(publicStore);

  /**
   * 의존성 주입
   */
  headerTabStore.setPublicStore(publicStore);
  actionStore.setModalStore(modalStore);
  waitQueueStore.setToolStore(toolStore);
  waitQueueStore.setModalStore(modalStore);
  publicStore.setModalStore(modalStore);
  publicStore.setActionStore(actionStore);
  publicStore.setToolStore(toolStore);

  /**
   * 에이전트 드라이버는 React 밖에서 돌기 때문에 Provider 로 주입받을 수 없다.
   * 열린 화면이 무엇이고 그 화면이 무슨 데이터를 들고 있는지만 알면 되므로
   * 필요한 함수만 내보낸다.
   *
   * publicStore 를 통째로 걸면 user.token 과 저장·삭제 메서드까지 같이 나간다.
   * 조회 결과를 읽는 데 필요하지 않은 것을 노출할 이유가 없다.
   */
  // '전체'(%)는 조회 조건용 와일드카드이지 사업장이 아니다. 순회 대상에 넣으면
  // 모든 사업장의 행을 한 번 더 더하게 된다.
  const realSpjangs = () => publicStore.spjangs
    .filter((site) => String(site.spjangcd ?? '') !== '%');
  // 순회를 시작하기 전의 전역 문맥. 선택지에 없는 값(전사 ZZ)일 수 있어 따로 들고 있다가
  // 그 값으로만 되돌아가는 것을 허용한다.
  let contextBeforeSwitch: { spjangcd: string; spjangnm: string } | null = null;

  (window as any).__elmanAgent = {
    currentPath: () => publicStore.currentMenu?.active?.path ?? null,
    pageState: () => (publicStore.currentPage as any)?.state ?? null,
    businessSites: () => realSpjangs().map((site) => ({
      code: String(site.spjangcd ?? ''),
      name: String(site.spjangnm ?? ''),
      current: String(site.spjangcd ?? '') === String(publicStore.user.spjangcd ?? ''),
    })),
    // 선택지에 없는 문맥(전사 ZZ)에 로그인해 있을 수 있다. 순회 뒤 돌아갈 곳이다.
    currentBusinessSite: () => String(publicStore.user.spjangcd ?? '') || null,
    switchBusinessSite: async (code: string) => {
      // 전역 문맥을 바꾸면 열린 모든 화면의 다음 조회에 영향을 준다. 작성 중인
      // 화면이 있으면 자동 전환하지 않고 사람이 먼저 정리하게 한다.
      if (publicStore.isStateChanged) return false;
      if (!contextBeforeSwitch) {
        contextBeforeSwitch = {
          spjangcd: String(publicStore.user.spjangcd ?? ''),
          spjangnm: String(publicStore.user.spjangnm ?? ''),
        };
      }
      const site = realSpjangs().find((candidate) => String(candidate.spjangcd) === code);
      // 목록에 없는 코드는 순회를 시작한 그 문맥으로 되돌아갈 때만 받아들인다.
      const target = site
        ? { spjangcd: String(site.spjangcd), spjangnm: String(site.spjangnm ?? '') }
        : (code === contextBeforeSwitch.spjangcd ? contextBeforeSwitch : null);
      if (!target) return false;
      await publicStore.setUser({
        ...publicStore.user,
        spjangcd: target.spjangcd,
        spjangnm: target.spjangnm,
      });
      return String(publicStore.user.spjangcd) === code;
    },
    // openChk 계열은 조회 조건이지만 일반 입력칸이 아니라 표의 행이다. 에이전트에
    // 임의 setState를 노출하지 않고, 행의 체크 값만 바꾸는 좁은 통로를 둔다.
    setCheckedRows: (stateKey: string, checkKey = 'chk', checked = true) => new Promise<boolean>((resolve) => {
      const page = publicStore.currentPage as any;
      const rows = page?.state?.[stateKey];
      if (!page?.setState || !Array.isArray(rows) || !rows.length) { resolve(false); return; }
      let changed = false;
      const next = rows.map((row: unknown) => {
        if (!row || typeof row !== 'object') return row;
        const record = row as Record<string, unknown>;
        if (!(checkKey in record)) return row;
        changed = true;
        return { ...record, [checkKey]: checked ? '1' : '0' };
      });
      if (!changed) { resolve(false); return; }
      page.setState({ [stateKey]: next }, () => resolve(true));
    }),
    // 한 행만 체크하는 통로. 입금입력은 갚을 미수 **한 건**을 골라야 하는데
    // `setCheckedRows` 는 전부를 켠다. 위젯을 눌러서는 상태가 바뀌지 않는다
    // (Lottie 라 합성 클릭이 먹지 않는 자리가 있다).
    setRowCheck: (stateKey: string, index: number, checkKey = 'chk', checked = true) => (
      new Promise<boolean>((resolve) => {
        const page = publicStore.currentPage as any;
        const rows = page?.state?.[stateKey];
        if (!page?.setState || !Array.isArray(rows) || !rows[index]) { resolve(false); return; }
        const record = rows[index] as Record<string, unknown>;
        if (!(checkKey in record)) { resolve(false); return; }
        const next = rows.slice();
        next[index] = { ...record, [checkKey]: checked ? '1' : '0' };
        page.setState({ [stateKey]: next }, () => resolve(true));
      })
    ),
    // 체크칸 하나만 켜고 끄는 통로. 이 앱의 `CheckBox` 는 `onChange` 를
    // `requestAnimationFrame` 안에서 부르고, 그 앞에 두 번 미룬다(직전에 만진 것이
    // 입력칸이면 300ms, 서버를 다녀오는 중이면 또 300ms). 합성 클릭이 그 사이에
    // 삼켜지면 아무 일도 일어나지 않는다 — 입금형태를 못 켜 입금이 저장되지 않았다.
    //
    // 값은 `'1'`/`'0'` 뿐이고, 이미 그 꼴인 키에만 쓴다. 임의 setState 가 아니다.
    setCheck: (name: string, checked = true) => new Promise<boolean>((resolve) => {
      const page = publicStore.currentPage as any;
      const state = page?.state;
      if (!page?.setState || !state) { resolve(false); return; }
      const holder = ['1', '0'].includes(String(state[name])) ? state : state.data;
      if (!holder || !['1', '0'].includes(String(holder[name]))) { resolve(false); return; }
      const value = checked ? '1' : '0';
      if (holder === state) {
        page.setState({ [name]: value }, () => resolve(true));
        return;
      }
      page.setState({ data: { ...holder, [name]: value } }, () => resolve(true));
    }),
    // 에이전트가 본인 목록의 과거 행을 선택해 상세를 읽는 좁은 읽기 통로다.
    // 임의 setState나 저장 메서드는 노출하지 않는다.
    focusRow: async (stateKey: string, match: Record<string, string>) => {
      const page = publicStore.currentPage as any;
      const rows = page?.state?.[stateKey];
      if (!Array.isArray(rows) || typeof page?.onRowFocusEvent !== 'function') return false;
      const index = rows.findIndex((row: unknown) => row && typeof row === 'object'
        && Object.entries(match).every(([key, value]) => (
          String((row as Record<string, unknown>)[key] ?? '') === value
        )));
      if (index < 0) return false;
      // **안쪽 표는 그 표의 처리기로 고른다.** 화면 행 처리기(`onRowFocusEvent`)는 바깥 목록 담당이다 — 보수현장의
      // 호기 줄을 넘기면 현장을 다시 읽고 첫 호기로 돌아가, 고른 호기가 안 잡혔다(2026-10-14). 화면이 알려 준
      // 표(`agentTables`)면 사람이 누른 것처럼 그 표의 포커스를 옮긴다.
      const table = page?.agentTables?.[stateKey]?.current;
      if (table && typeof table.setFocus === 'function') {
        table.setFocus(index);
        return true;
      }
      page.onRowFocusEvent(rows[index], index);
      return true;
    },
    openTabs: () => headerTabStore.tabs.map((tab) => ({
      path: tab.data.menu.path,
      name: String(tab.data.menu.text ?? ''),
    })),
    // 조회가 끝났는지 화면 상태만 보고 알아내려 하면 틀린다. 조회를 누르면
    // 표를 먼저 비우므로, 빈 채로 서버를 기다리는 것과 정말 결과가 없는 것이
    // 구분되지 않는다. 앱이 이미 아는 사실을 그대로 알려준다.
    busy: () => publicStore.isUsingNetwork,
    // 부서명은 에이전트가 만드는 공지의 발신·문의처와 보고서 조치의 담당이 된다.
    me: () => {
      const { user } = publicStore;
      if (!user?.perid) return null;
      return {
        perid: user.perid,
        pernm: String(user.pernm ?? ''),
        spjangcd: user.spjangcd ? String(user.spjangcd) : undefined,
        spjangnm: user.spjangnm || undefined,
        divinm: user.divinm || undefined,
      };
    },
    /**
     * 그 화면에 닿기까지 펼쳐야 할 상위 메뉴들의 id. 위에서 아래 순서다.
     *
     * 화면 앵커는 폴더를 펼치기 전에는 DOM 에 없다. 이것이 없던 동안 드라이버는
     * 모듈과 폴더를 하나씩 눌러 보며 찾았고, 못 찾으면 메뉴를 다 펼친 채로 끝났다.
     * 트리는 앱이 이미 들고 있으니 어디에 있는지만 알려준다 — 여는 것은 여전히
     * 드라이버가 클릭으로 한다.
     */
    menuChain: (path: string) => {
      const chain: string[] = [];
      const walk = (nodes: MenuModel[]): boolean => nodes.some((node) => {
        if (node.path === path) return true;
        if (node.children?.length && walk(node.children)) {
          chain.unshift(node.id);
          return true;
        }
        return false;
      });
      return walk(menuStore.menus) ? chain : null;
    },

    /**
     * 사람이 이번 대화에 올린 파일을 **지금 화면의 문서**에 붙인다.
     *
     * 문서 번호도 업로드 절차도 첨부 상자가 안다(`attachTarget`). 여기서 하는 일은
     * 이번 턴의 파일을 상자에 넘기는 것뿐이다.
     *
     * **던지지 않는다.** 못 붙이면 이유를 돌려준다 — 대화가 죽는 것보다 낫다.
     */
    /**
     * 서버에 맡긴 오래 걸리는 일의 대기열. 드라이버가 버튼·저장 뒤 새로 올라간 일을 알아보는 데
     * 쓴다. **읽기만** — 결과(`response`)와 완료 뒤 할 일(`callback`)은 내보내지 않는다.
     */
    waitQueue: () => waitQueueStore.data.map((item) => ({
      key: item.key,
      title: item.title,
      startedAt: item.intime.getTime(),
      done: Boolean(item.isDone),
    })),
    attach: async () => {
      const files = agentStore.filesOfTurn();
      if (!files.length) return { attached: 0, notices: ['이번 대화에 올린 파일이 없습니다.'] };
      const target = attachTarget();
      if (!target) {
        // **왜 없는지까지 말한다.** 「붙일 곳이 없습니다」만으로는 문서를 안 골랐는지
        // 창이 닫혔는지 알 수 없어, 사람이 무엇을 해야 할지 모른다.
        const found = attachDiagnosis();
        const why = found.total === 0
          ? '이 화면에는 파일을 붙일 곳이 없습니다.'
          : (found.ready === 0
            ? '먼저 파일을 붙일 문서를 골라 주세요.'
            : '첨부 상자가 화면에 보이지 않습니다. 첨부 탭이나 첨부 창을 열어 주세요.');
        return { attached: 0, notices: [why] };
      }
      try {
        await target.accept(files.map((one) => new File(
          [Uint8Array.from(atob(one.data), (ch) => ch.charCodeAt(0))],
          one.name,
          { type: one.mime },
        )));
      } catch (error) {
        return {
          attached: 0,
          notices: [`파일을 붙이지 못했습니다: ${error instanceof Error ? error.message : String(error)}`],
        };
      }
      return { attached: files.length, notices: [] };
    },
  };

  // 패널이 부를 함수를 단다. 자격증명은 넘기지 않고 **누구인지**만 넘긴다 —
  // 모델 키와 검색 키는 미들서버에 있다.
  installAgent(() => {
    const { user } = publicStore;
    if (!user?.custcd || !user?.perid || !user?.token) return null;
    return { custcd: user.custcd, perid: user.perid, token: user.token };
  });

  /**
   * **에이전트가 지금 도는 중인가.** 잣대가 이것으로 턴의 끝을 안다.
   *
   * 예전에는 잣대가 입력창의 `readOnly` 로 판단했다. 그런데 앞 작업 중에도 질문을
   * 칠 수 있게 하면서 `readonly` 에서 `busy` 를 뺐고(2026-09-21), 그날부터 잣대는
   * **턴이 시작하기도 전에 판정**했다 — 쓰기 4건이 0/4 로 나왔고 답으로 읽힌 것이
   * 입력창 안내 문구였다. 화면 모양에 기대면 이렇게 조용히 깨진다.
   *
   * 값만 돌려준다. 자격증명도, 대화 내용도 나가지 않는다.
   */
  (window as unknown as { __elmanAgentBusy?: () => boolean }).__elmanAgentBusy = () => agentStore.busy;

  /**
   * 사용 기록기(2026-09-29). 에이전트와 같은 방법으로 **누구인지**만 넘긴다 — 로그인 전이면
   * null 이라 기록하지 않는다. 비밀번호는 기록기 쪽에서 거른다.
   */
  installRecorder(() => {
    const { user } = publicStore;
    if (!user?.custcd || !user?.perid || !user?.token) return null;
    return { custcd: user.custcd, perid: user.perid, token: user.token };
  });

  return {
    actionStore,
    agentStore,
    headerTabStore,
    menuStore,
    modalStore,
    publicStore,
    routerStore,
    toolStore,
    waitQueueStore,
    favoriteMenuStore,
    translateStore: t,
  };
}
